import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyPublicLinkButton,
  Input,
  Label,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  useAuth,
} from '@yeshuman/sdk'
import { Globe, Loader2, Lock, LogOut } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  publicProfileUrl,
  useCloseAccount,
  usePrivacySettings,
  useUpdatePrivacySettings,
  type ProfileVisibility,
} from '../../hooks/use-privacy-settings'

const VISIBILITY_OPTIONS: {
  value: ProfileVisibility
  title: string
  description: string
  icon: typeof Lock
}[] = [
  {
    value: 'employers',
    title: 'Signed-in employers only (recommended)',
    description:
      'Employers, recruiters and interviewers signed in to TalentCo can open your profile. Anyone else who opens your link sees a "private profile" message.',
    icon: Lock,
  },
  {
    value: 'public',
    title: 'Anyone with the link',
    description:
      'Anyone with your link can see your profile, without signing in. Choose this to share your profile on LinkedIn, in a CV or by email.',
    icon: Globe,
  },
]

export function ProfileVisibilityExplainer() {
  return (
    <p className="text-sm text-muted-foreground">
      Your TalentCo profile page shows your name, photo, headline, location, skills, career history,
      education and certifications. Your email address and phone number are never shown on it.
    </p>
  )
}

export function CandidatePrivacyPage() {
  const settingsQuery = usePrivacySettings()
  const updateSettings = useUpdatePrivacySettings()
  const settings = settingsQuery.data
  // Reflect the click straight away while the save is in flight.
  const visibility =
    (updateSettings.isPending ? updateSettings.variables?.profile_visibility : undefined) ??
    settings?.profile_visibility

  const chooseVisibility = (value: ProfileVisibility) => {
    if (!settings || visibility === value) return
    updateSettings.mutate(
      { profile_visibility: value, acknowledge_notice: true },
      {
        onSuccess: () =>
          toast.success(
            value === 'public'
              ? 'Anyone with your link can now see your profile.'
              : 'Your profile is now visible to signed-in employers only.',
          ),
        onError: (error) => toast.error(error.message),
      },
    )
  }

  return (
    <PageContainer>
      <PageHeader leading={<PageHeaderLeading />} />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Who can see your profile</CardTitle>
            <CardDescription alwaysShow>
              Choose who can open your shareable profile link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ProfileVisibilityExplainer />
            {settingsQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Loading your settings…
              </p>
            ) : settingsQuery.isError ? (
              <p className="text-sm text-destructive">{settingsQuery.error.message}</p>
            ) : settings ? (
              <fieldset className="space-y-3" disabled={updateSettings.isPending}>
                <legend className="sr-only">Profile visibility</legend>
                {VISIBILITY_OPTIONS.map((option) => {
                  const selected = visibility === option.value
                  const Icon = option.icon
                  return (
                    <label
                      key={option.value}
                      className={
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ' +
                        (selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50')
                      }
                    >
                      <input
                        type="radio"
                        name="profile-visibility"
                        value={option.value}
                        checked={selected}
                        onChange={() => chooseVisibility(option.value)}
                        className="mt-1 size-4 accent-primary"
                      />
                      <span className="space-y-1">
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <Icon className="size-4" aria-hidden />
                          {option.title}
                        </span>
                        <span className="block text-sm text-muted-foreground">{option.description}</span>
                      </span>
                    </label>
                  )
                })}
                {visibility === 'public' && settings.public_slug ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-3 text-sm">
                    <span className="text-muted-foreground">Your public link:</span>
                    <a
                      href={publicProfileUrl(settings.public_slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 break-all font-medium underline-offset-4 hover:underline"
                    >
                      {publicProfileUrl(settings.public_slug)}
                    </a>
                    <CopyPublicLinkButton url={publicProfileUrl(settings.public_slug)} />
                  </div>
                ) : visibility === 'public' ? (
                  <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                    Your public link will appear here once you add your name in{' '}
                    <a href="/settings/account" className="font-medium underline underline-offset-4">
                      Settings → Account
                    </a>
                    .
                  </p>
                ) : null}
              </fieldset>
            ) : null}
          </CardContent>
        </Card>

        <CloseAccountCard
          canClose={settings?.can_close_account ?? false}
          blockedReason={settings?.close_blocked_reason ?? null}
          requiresPassword={settings?.requires_password ?? true}
          loading={settingsQuery.isLoading}
        />
      </div>
    </PageContainer>
  )
}

function CloseAccountCard({
  canClose,
  blockedReason,
  requiresPassword,
  loading,
}: {
  canClose: boolean
  blockedReason: string | null
  requiresPassword: boolean
  loading: boolean
}) {
  const { logout } = useAuth()
  const closeAccount = useCloseAccount()
  const [open, setOpen] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [password, setPassword] = useState('')

  const ready = confirm.trim().toUpperCase() === 'CLOSE' && (!requiresPassword || password.length > 0)

  const reset = () => {
    setConfirm('')
    setPassword('')
    closeAccount.reset()
  }

  const submit = () => {
    closeAccount.mutate(
      { confirm: confirm.trim(), password: requiresPassword ? password : undefined },
      {
        onSuccess: () => {
          setOpen(false)
          logout()
          window.location.assign('/?account_closed=1')
        },
      },
    )
  }

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle>Close account</CardTitle>
        <CardDescription alwaysShow>
          Close your TalentCo account and remove yourself from the platform.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          You will be signed out and won't be able to sign in again. Employers will no longer find
          you in search or matching, your profile link stops working, and any applications still
          in progress are withdrawn.
        </p>
        {blockedReason ? <p className="text-sm text-destructive">{blockedReason}</p> : null}
        <Button
          variant="destructive"
          disabled={loading || !canClose}
          onClick={() => {
            reset()
            setOpen(true)
          }}
        >
          <LogOut className="size-4" aria-hidden />
          Close my account
        </Button>
      </CardContent>

      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (closeAccount.isPending) return
          setOpen(next)
          if (!next) reset()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close your account?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be signed out and hidden from employers straight away, and your applications
              in progress are withdrawn.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (ready && !closeAccount.isPending) submit()
            }}
          >
            {requiresPassword ? (
              <div className="space-y-2">
                <Label htmlFor="close-account-password">Your password</Label>
                <Input
                  id="close-account-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="close-account-confirm">
                Type <span className="font-mono font-semibold">CLOSE</span> to confirm
              </Label>
              <Input
                id="close-account-confirm"
                autoComplete="off"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </div>
            {closeAccount.isError ? (
              <p className="text-sm text-destructive" role="alert">
                {closeAccount.error.message}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel type="button" disabled={closeAccount.isPending}>
                Cancel
              </AlertDialogCancel>
              <Button type="submit" variant="destructive" disabled={!ready || closeAccount.isPending}>
                {closeAccount.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                Close account
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
